'use client';

import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { Box, Divider, Link, Snackbar, Typography, useTheme } from '@mui/material';
import { MouseEvent, ReactNode, useState } from 'react';

import { removeTrailingBackslash } from '@/lib/UrlHelpers';

interface ArticleShareLinksProps {
  title: string;
  path: string;
  contentLabel: 'article' | 'page';
  enabledNetworks?: {
    facebook?: boolean | null;
    instagram?: boolean | null;
    linkedIn?: boolean | null;
    mail?: boolean | null;
    x?: boolean | null;
  };
  showLabels?: boolean;
}

function getShareUrl(path: string) {
  if (typeof window !== 'undefined') return window.location.href.split('#')[0];
  const baseUrl = process.env.NEXT_PUBLIC_FULL_APP_URL
    ? removeTrailingBackslash(process.env.NEXT_PUBLIC_FULL_APP_URL)
    : '';

  return baseUrl && path ? `${baseUrl}${path}` : path;
}

export function ArticleShareLinks({
  title,
  path,
  contentLabel,
  enabledNetworks,
  showLabels = true,
}: ArticleShareLinksProps) {
  const theme = useTheme();
  const [copyMessageOpen, setCopyMessageOpen] = useState(false);
  const shareUrl = getShareUrl(path);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    enabledNetworks?.mail !== false && {
      label: 'Mail',
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      icon: <EmailIcon />,
    },
    enabledNetworks?.facebook !== false && {
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: <FacebookIcon />,
    },
    enabledNetworks?.instagram !== false && {
      label: 'Instagram',
      ariaLabel: 'Copy link for Instagram',
      action: 'copy',
      icon: <InstagramIcon />,
    },
    enabledNetworks?.x !== false && {
      label: 'X',
      ariaLabel: 'Share on X',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: <XIcon />,
    },
    enabledNetworks?.linkedIn !== false && {
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: <LinkedInIcon />,
    },
  ].filter(Boolean) as Array<{
    action?: 'copy';
    ariaLabel?: string;
    href?: string;
    icon: ReactNode;
    label: string;
  }>;

  const copyShareUrl = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    setCopyMessageOpen(true);
  };

  if (!links.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2.5, borderColor: theme.palette.purple[200] }} />
      <Typography
        variant="body2"
        sx={{ mb: 2, textAlign: 'center', color: theme.palette.text.secondary }}
      >
        Share this {contentLabel}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
          gap: showLabels ? 0.75 : 2,
          minWidth: 0,
        }}
      >
        {links.map((item) => (
          <Link
            key={item.label}
            href={item.action === 'copy' ? '#' : item.href}
            target={item.action === 'copy' || item.label === 'Mail' ? undefined : '_blank'}
            rel={
              item.action === 'copy' || item.label === 'Mail' ? undefined : 'noopener noreferrer'
            }
            aria-label={item.ariaLabel ?? item.label}
            onClick={item.action === 'copy' ? copyShareUrl : undefined}
            underline="none"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: '0 1 2.75rem',
              minWidth: 0,
              maxWidth: '2.75rem',
              gap: showLabels ? 0.75 : 0,
              color: theme.palette.brand.primarySecondary1,
              fontSize: 10,
              lineHeight: 1.2,
              textAlign: 'center',
              overflowWrap: 'anywhere',
              '& svg': {
                fontSize: 20,
              },
            }}
          >
            {item.icon}
            {showLabels && item.label}
          </Link>
        ))}
      </Box>
      <Snackbar
        open={copyMessageOpen}
        autoHideDuration={2500}
        onClose={() => setCopyMessageOpen(false)}
        message={`${contentLabel === 'article' ? 'Article' : 'Page'} link copied`}
      />
    </Box>
  );
}
